import * as React from 'react';
import { Icon } from '../../display';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Days of the week abbreviations
 */
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/**
 * Month names
 */
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export interface CalendarProps {
  /**
   * Currently selected date (for single date selection)
   */
  selectedDate?: Date | null;
  /**
   * Start date of selected range (for range selection)
   */
  rangeStart?: Date | null;
  /**
   * End date of selected range (for range selection)
   */
  rangeEnd?: Date | null;
  /**
   * Date currently being hovered (for range preview)
   */
  hoverDate?: Date | null;
  /**
   * Callback when a date is selected
   */
  onDateSelect?: (date: Date) => void;
  /**
   * Callback when hovering over a date
   */
  onDateHover?: (date: Date | null) => void;
  /**
   * Minimum selectable date
   */
  minDate?: Date;
  /**
   * Maximum selectable date
   */
  maxDate?: Date;
  /**
   * Array of dates that should be disabled
   */
  disabledDates?: Date[];
  /**
   * The month/year to display (controlled)
   */
  viewDate?: Date;
  /**
   * Callback when the view month changes
   */
  onViewDateChange?: (date: Date) => void;
  /**
   * Whether range mode is enabled
   */
  isRangeMode?: boolean;
  /**
   * Callback for Today button click
   */
  onTodayClick?: () => void;
}

/**
 * Check if two dates are the same day
 */
function isSameDay(date1: Date | null | undefined, date2: Date | null | undefined): boolean {
  if (!date1 || !date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Check if a date is today
 */
function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

/**
 * Check if date is within range
 */
function isInRange(
  date: Date,
  start: Date | null | undefined,
  end: Date | null | undefined
): boolean {
  if (!start || !end) return false;
  const time = date.getTime();
  const startTime = start.getTime();
  const endTime = end.getTime();
  return time > Math.min(startTime, endTime) && time < Math.max(startTime, endTime);
}

/**
 * Check if a date is disabled
 */
function isDateDisabled(
  date: Date,
  minDate?: Date,
  maxDate?: Date,
  disabledDates?: Date[]
): boolean {
  if (minDate && date < minDate) {
    // Check if it's before the min date (comparing dates without time)
    const minDateStart = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (dateStart < minDateStart) return true;
  }
  if (maxDate) {
    const maxDateEnd = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    const dateEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (dateEnd > maxDateEnd) return true;
  }
  if (disabledDates) {
    return disabledDates.some((d) => isSameDay(d, date));
  }
  return false;
}

/**
 * Get calendar days for a month, including days from previous/next months
 * to fill the 6-row grid
 */
function getCalendarDays(year: number, month: number): Date[] {
  const days: Date[] = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Days from previous month to fill the first week
  const startDayOfWeek = firstDay.getDay();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    days.push(new Date(year, month, -i));
  }

  // Days of current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(new Date(year, month, i));
  }

  // Days from next month to fill remaining cells (6 rows = 42 cells)
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push(new Date(year, month + 1, i));
  }

  return days;
}

/**
 * Calendar component for date picking.
 *
 * Features:
 * - Month/year navigation
 * - Today highlighting (outlined in primary-400)
 * - Selected date highlighting (filled primary-700)
 * - Range selection with in-range highlighting (primary-100)
 * - Keyboard navigation support
 * - Today button for quick navigation
 *
 * @example
 * // Single date selection
 * <Calendar
 *   selectedDate={selectedDate}
 *   onDateSelect={setSelectedDate}
 * />
 *
 * @example
 * // Range selection
 * <Calendar
 *   rangeStart={startDate}
 *   rangeEnd={endDate}
 *   onDateSelect={handleDateSelect}
 *   isRangeMode
 * />
 */
const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      selectedDate,
      rangeStart,
      rangeEnd,
      hoverDate,
      onDateSelect,
      onDateHover,
      minDate,
      maxDate,
      disabledDates,
      viewDate: controlledViewDate,
      onViewDateChange,
      isRangeMode = false,
      onTodayClick,
    },
    ref
  ) => {
    // Internal view date state
    const [internalViewDate, setInternalViewDate] = React.useState(
      () => selectedDate || rangeStart || new Date()
    );

    // Use controlled or internal view date
    const viewDate = controlledViewDate || internalViewDate;
    const setViewDate = (date: Date) => {
      if (onViewDateChange) {
        onViewDateChange(date);
      } else {
        setInternalViewDate(date);
      }
    };

    const viewYear = viewDate.getFullYear();
    const viewMonth = viewDate.getMonth();
    const calendarDays = getCalendarDays(viewYear, viewMonth);

    // Navigation handlers
    const goToPreviousMonth = () => {
      setViewDate(new Date(viewYear, viewMonth - 1, 1));
    };

    const goToNextMonth = () => {
      setViewDate(new Date(viewYear, viewMonth + 1, 1));
    };

    const goToToday = () => {
      const today = new Date();
      setViewDate(today);
      if (onTodayClick) {
        onTodayClick();
      }
    };

    // Keyboard navigation
    const [focusedDate, setFocusedDate] = React.useState<Date | null>(null);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      /* v8 ignore start */
      if (!focusedDate) return;
      /* v8 ignore stop */

      let newDate: Date | null = null;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          newDate = new Date(focusedDate);
          newDate.setDate(newDate.getDate() - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          newDate = new Date(focusedDate);
          newDate.setDate(newDate.getDate() + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          newDate = new Date(focusedDate);
          newDate.setDate(newDate.getDate() - 7);
          break;
        case 'ArrowDown':
          e.preventDefault();
          newDate = new Date(focusedDate);
          newDate.setDate(newDate.getDate() + 7);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isDateDisabled(focusedDate, minDate, maxDate, disabledDates)) {
            onDateSelect?.(focusedDate);
          }
          return;
        case 'Escape':
          e.preventDefault();
          setFocusedDate(null);
          return;
      }

      if (newDate) {
        // Update view if navigating to different month
        if (newDate.getMonth() !== viewMonth || newDate.getFullYear() !== viewYear) {
          setViewDate(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
        }
        setFocusedDate(newDate);
      }
    };

    // Get day button classes
    const getDayClasses = (date: Date): string => {
      const isCurrentMonth = date.getMonth() === viewMonth;
      const isSelected = isSameDay(date, selectedDate);
      const isRangeStartDay = isSameDay(date, rangeStart);
      const isRangeEndDay = isSameDay(date, rangeEnd);
      const isRangeEdge = isRangeStartDay || isRangeEndDay;
      const inRange = isRangeMode && isInRange(date, rangeStart, rangeEnd || hoverDate);
      const isTodayDate = isToday(date);
      const isDisabled = isDateDisabled(date, minDate, maxDate, disabledDates);
      const isFocused = isSameDay(date, focusedDate);

      const classes = [
        // Base styles
        'w-9',
        'h-9',
        'flex',
        'items-center',
        'justify-center',
        'text-sm',
        'rounded-full',
        'transition-colors',
        'duration-150',
        'ease-out',
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-primary-200', // primary-200
        'focus:ring-offset-1',
      ];

      if (isDisabled) {
        classes.push('text-secondary-400', 'cursor-not-allowed'); // neutral-400
      } else if (isSelected || isRangeEdge) {
        // Selected or range edge: filled primary-700
        classes.push('bg-primary-700', 'text-white', 'hover:bg-primary-800'); // primary-700 -> primary-800
      } else if (inRange) {
        // In range: background primary-100
        classes.push('bg-primary-100', 'text-primary-700', 'hover:bg-primary-200'); // primary-100
      } else if (isTodayDate) {
        // Today: outlined in primary-400
        classes.push(
          'border-2',
          'border-primary-400', // primary-400
          isCurrentMonth ? 'text-primary-700' : 'text-secondary-400',
          'hover:bg-primary-50' // primary-50
        );
      } else if (isCurrentMonth) {
        classes.push('text-secondary-700', 'hover:bg-primary-50'); // neutral-700, primary-50
      } else {
        // Other month days
        classes.push('text-secondary-400', 'hover:bg-secondary-100'); // neutral-400, neutral-100
      }

      if (isFocused && !isDisabled) {
        classes.push('ring-2', 'ring-primary-200');
      }

      return classes.join(' ');
    };

    return (
      <div
        ref={ref}
        className="w-[280px] bg-secondary-100 rounded-lg shadow-lg border border-secondary-200 p-3"
        role="application"
        aria-label="Calendar"
      >
        {/* Header: Month/Year with navigation */}
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={goToPreviousMonth}
            className="p-1.5 rounded-full hover:bg-secondary-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-200"
            aria-label="Previous month"
          >
            <Icon icon={ChevronLeft} size="sm" color="secondary" />
          </button>

          <span className="text-sm font-medium text-secondary-800">
            {MONTHS[viewMonth]} {viewYear}
          </span>

          <button
            type="button"
            onClick={goToNextMonth}
            className="p-1.5 rounded-full hover:bg-secondary-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-200"
            aria-label="Next month"
          >
            <Icon icon={ChevronRight} size="sm" color="secondary" />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-0 mb-1">
          {WEEKDAYS.map((day) => (
            <div
              key={day}
              className="w-9 h-8 flex items-center justify-center text-xs font-medium text-secondary-500"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-0" role="grid" aria-label="Calendar days">
          {calendarDays.map((date) => {
            const isDisabled = isDateDisabled(date, minDate, maxDate, disabledDates);
            return (
              <button
                key={date.toISOString()}
                type="button"
                className={getDayClasses(date)}
                onClick={() => !isDisabled && onDateSelect?.(date)}
                onMouseEnter={() => !isDisabled && onDateHover?.(date)}
                onMouseLeave={() => onDateHover?.(null)}
                onFocus={() => setFocusedDate(date)}
                onKeyDown={handleKeyDown}
                disabled={isDisabled}
                tabIndex={isSameDay(date, focusedDate) ? 0 : -1}
                aria-label={date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
                aria-current={
                  isSameDay(date, selectedDate) ||
                  isSameDay(date, rangeStart) ||
                  isSameDay(date, rangeEnd)
                    ? 'date'
                    : undefined
                }
                aria-disabled={isDisabled}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>

        {/* Today button */}
        <div className="mt-3 pt-3 border-t border-secondary-200">
          <button
            type="button"
            onClick={goToToday}
            className="w-full py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            Today
          </button>
        </div>
      </div>
    );
  }
);

Calendar.displayName = 'Calendar';

export { Calendar };
